// Training Hub dispatcher. Reads the current user's effective
// `training_program` from /api/training/mode and routes them to the
// matching UI:
//   - "tfvf"       → Total Fusion curriculum (TfvfShell + sub-pages)
//   - "kb_generic" → KB-driven training (KbTrainingShell + KbTrainingHome/Detail)
//   - "none"       → clean "training not enabled" screen with a link home
//
// Legacy `/training/:legacyId` URLs still redirect to the TFVF module
// map when the tenant is on TFVF; on kb_generic they route into the
// KB detail page keyed by kb_id if the segment is a UUID, else fall
// back to the home.
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Navigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { GraduationCap, Loader2 } from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';

// TFVF sub-pages
import TfvfShell from './tfvf/TfvfShell';
import TfvfHome from './tfvf/TfvfHome';
import TfvfModule from './tfvf/TfvfModule';
import TfvfPlaybook from './tfvf/TfvfPlaybook';
import TfvfAssets from './tfvf/TfvfAssets';
import TfvfManager from './tfvf/TfvfManager';
import TfvfAdmin from './tfvf/TfvfAdmin';
import TfvfAcks from './tfvf/TfvfAcks';
import TfvfPractice from './tfvf/TfvfPractice';
import TfvfPractical from './tfvf/TfvfPractical';
import TfvfCertification from './tfvf/TfvfCertification';
import TfvfRetraining from './tfvf/TfvfRetraining';

// KB-driven sub-pages
import KbTrainingShell from './kb/KbTrainingShell';
import KbTrainingHome from './kb/KbTrainingHome';
import KbTrainingDetail from './kb/KbTrainingDetail';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEGACY_REDIRECT = {
  sales_playbook:          'TFVF-03',
  green_brain:             'TFVF-04',
  disc_matrix:             'TFVF-05',
  rule_of_three:           'TFVF-06',
  objection_playbook:      'TFVF-06',
  manager_coaching_manual: 'TFVF-M1',
  retraining_workflow:     'TFVF-M1',
  executive_summary:       'TFVF-08',
  dashboard_walkthrough:   'TFVF-08',
  orientation:             'TFVF-00',
  how_voicefabric_works:   'TFVF-01',
  staff_manual:            'TFVF-01',
  scoring_appeals:         'TFVF-01',
};

const RESERVED_PATHS = new Set(['playbook','assets','manager','admin','acknowledgements','practice','practical','certification','kb','retraining']);

const TrainingHubPage = () => {
  const params = useParams();
  const location = useLocation();
  const first = params.itemId;
  // The `/training/kb/:kbId` route puts the KB id in params.kbId; the
  // pathname check is a fallback for the case where react-router's
  // route ordering resolved to `/training/:itemId` with itemId='kb'
  // (older browsers, hot-reload edge cases).
  const kbIdFromRoute = params.kbId || (location.pathname.startsWith('/training/kb/') ? location.pathname.split('/training/kb/')[1] : null);
  const { token } = useAuth();
  const [mode, setMode] = useState(null); // { program, branding, domain_id }

  useEffect(() => {
    axios
      .get(`${API}/training/mode`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setMode(r.data))
      .catch(() => setMode({ error: true }));
  }, [token]);

  const tfvfBody = useMemo(() => {
    if (!first) return <TfvfHome />;
    if (first === 'playbook')          return <TfvfPlaybook />;
    if (first === 'assets')            return <TfvfAssets />;
    if (first === 'manager')           return <TfvfManager />;
    if (first === 'admin')             return <TfvfAdmin />;
    if (first === 'acknowledgements')  return <TfvfAcks />;
    if (first === 'practice')          return <TfvfPractice />;
    if (first === 'practical')         return <TfvfPractical />;
    if (first === 'certification')     return <TfvfCertification />;
    if (first === 'retraining')        return <TfvfRetraining />;
    if (first.toUpperCase().startsWith('TFVF-')) return <TfvfModule />;
    const mapped = LEGACY_REDIRECT[first];
    if (mapped) return <Navigate to={`/training/${mapped}`} replace />;
    if (RESERVED_PATHS.has(first)) return <TfvfHome />;
    return <Navigate to="/training" replace />;
  }, [first]);

  if (!mode) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-8 text-center" data-testid="training-mode-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-500" />
      </div>
    );
  }

  if (mode.error || mode.program === 'none') {
    return (
      <div
        className="rounded-xl border border-slate-200 bg-white p-10 text-center max-w-lg mx-auto"
        data-testid="training-not-enabled"
      >
        <GraduationCap className="w-10 h-10 mx-auto text-slate-400 mb-3" />
        <h2 className="text-lg font-bold text-slate-900">Training is not enabled yet</h2>
        <p className="text-sm text-slate-600 mt-1">
          Your admin hasn&apos;t configured a training program for this workspace. Once they add one,
          your modules will appear here automatically.
        </p>
        <Link
          to="/"
          className="mt-4 inline-block text-xs font-semibold text-slate-700 hover:text-slate-900 underline"
        >
          Back to dashboard
        </Link>
      </div>
    );
  }

  if (mode.program === 'kb_generic') {
    return (
      <KbTrainingShell branding={mode.branding}>
        {kbIdFromRoute ? <KbTrainingDetail token={token} /> : <KbTrainingHome token={token} />}
      </KbTrainingShell>
    );
  }

  // Default: TFVF
  return <TfvfShell>{tfvfBody}</TfvfShell>;
};

export default TrainingHubPage;
